/**
 * Certificate Lifecycle Classification Rules
 *
 * Maritime certificates follow different lifecycle patterns. This module encodes
 * official DG Shipping India and IMO STCW rules deterministically — no AI is used
 * for compliance decisions at runtime.
 *
 * Lifecycle categories:
 *   no_expiry               — Permanent records (INDoS, EXN-45).
 *   expiry_date_based       — Standard: governed purely by the date on the certificate.
 *   refresher_required      — Course must be refreshed on a fixed cycle to stay valid.
 *   revalidation_required   — Competency must be revalidated (sea service + update training).
 *   no_five_year_revalidation — Reserved for certificates with genuinely ambiguous renewal
 *                               cycles where auto-expiring by age alone is incorrect.
 *   unknown_verify          — Fallback: prompt the seafarer to verify with employer.
 *
 * Rule ordering: first match wins. More-specific patterns must appear before broader ones.
 *
 * References:
 *   STCW 2010 Manila Amendments:
 *     https://www.imo.org/en/ourwork/humanelement/pages/stcw-conv-link.aspx
 *   DG Shipping STCW Circular No. 6 of 2011:
 *     https://www.dgshipping.gov.in/viewNotice.aspx?noticeid=976
 *   DG Shipping INDoS Notice:
 *     https://dgshipping.gov.in/Content/viewNotice.aspx?noticeid=916
 *   DGS Training Circular No. 04 of 2025 (EXN-45 discontinuation):
 *     https://dgshipping.gov.in/WriteReadData/userfiles/file/Training%20Cir-No4%20of%202025-Discontinuation%20of%20Exit%20Exams.pdf
 */

import { normalizeCertificateName } from '@/data/certificate-catalog';

export type LifecycleType =
  | 'no_expiry'
  | 'expiry_date_based'
  | 'refresher_required'
  | 'revalidation_required'
  | 'no_five_year_revalidation'
  | 'unknown_verify';

export interface LifecycleRule {
  /** Stable identifier used as sourceRuleId on the Certificate object */
  id: string;
  /** Matched against the certificate/course name (case-insensitive) */
  test: RegExp;
  lifecycle: LifecycleType;
  /**
   * True only when an explicit issuing-authority expiry should override an
   * otherwise no-expiry rule. INDoS remains lifetime even if a parser finds a date.
   */
  honorExplicitExpiry?: boolean;
  /** Renewal cycle length in years where applicable */
  cycleYears?: number;
  /** Default UI label — may be refined by resolveActionLabel() based on status */
  actionLabel: string;
  /** Human-readable regulatory basis note stored on the Certificate */
  requirementNote: string;
}

export const LIFECYCLE_RULES: LifecycleRule[] = [

  // ═══════════════════════════════════════════════════════════════════════════
  // PERMANENT SEAFARER RECORDS
  // ═══════════════════════════════════════════════════════════════════════════

  // INDoS: lifetime DG Shipping identifier assigned once to every Indian seafarer.
  // Ref: DG Shipping INDoS Notice (noticeid=916)
  {
    id: 'indos',
    test: /indos|indian national database of seafarers/i,
    lifecycle: 'no_expiry',
    actionLabel: 'No expiry',
    requirementNote:
      'INDoS is a permanent DG Shipping seafarer identifier. It does not carry an expiry date.',
  },

  // EXN-45: DG Shipping exit-exam notation. Exit exam discontinued per DGS Training
  // Circular No. 04 of 2025. No ongoing renewal unless the issuing authority
  // provided an explicit expiry date; in that case, the explicit date wins.
  {
    id: 'exn45',
    test: /exn.?45|\bexn\b/i,
    lifecycle: 'no_expiry',
    honorExplicitExpiry: true,
    actionLabel: 'No expiry',
    requirementNote:
      'EXN-45 exit-exam record. No ongoing renewal per DGS Training Circular No. 04 of 2025.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BASIC SAFETY TRAINING — only specified practical competencies have a
  // fixed five-year refresher under DGS rules.
  // ═══════════════════════════════════════════════════════════════════════════

  // PST — Personal Survival Techniques (STCW Table A-VI/1-1)
  {
    id: 'pst',
    test: /personal survival techniques|\bpst\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/1 Table A-VI/1-1: 5-year refresher in Personal Survival Techniques required.',
  },

  // FPFF — Fire Prevention & Fire Fighting (STCW Table A-VI/1-2)
  // Must appear before AFF to avoid cross-matching.
  {
    id: 'fpff',
    test: /fire\s*prevention\s*(?:and|&)\s*fire\s*[- ]?\s*fighting|\bfp\s*&?\s*ff\b|\bfpff\b|basic\s+fire\s*[- ]?\s*fighting/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/1 Table A-VI/1-2: 5-year refresher in Fire Prevention & Fire Fighting required.',
  },

  // EFA — Elementary First Aid (STCW Table A-VI/1-3)
  // Must appear before MFA since MFA subsumes EFA (superseding rule in builder handles removal).
  {
    id: 'efa',
    test: /elementary first aid|\befa\b/i,
    lifecycle: 'no_five_year_revalidation',
    actionLabel: 'Verify requirement',
    requirementNote:
      'DGS does not list EFA among the competencies requiring a fixed five-year refresher. MFA satisfies the lower EFA requirement; verify any company or flag requirement.',
  },

  // PSSR — Personal Safety & Social Responsibilities (STCW Table A-VI/1-4)
  {
    id: 'pssr',
    test: /personal safety.*social|social.*personal safety|\bpssr\b/i,
    lifecycle: 'no_five_year_revalidation',
    actionLabel: 'Verify requirement',
    requirementNote:
      'DGS does not list PSSR among the competencies requiring a fixed five-year refresher. Verify any company-specific requirement.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADVANCED FIRE FIGHTING — STCW Reg. VI/3  (officers, 5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: 'aff',
    test: /advanc(?:e|ed)(?:\s+training\s+(?:for|in)\s+)?\s*fire\s*[- ]?\s*fighting|advanc(?:e|ed)\s+firefighting|fire\s*[- ]?\s*fighting.*advanc(?:e|ed)|\baff\b|\badff\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/3: 5-year refresher in Advanced Fire Fighting required for designated officers.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SURVIVAL CRAFT & RESCUE — STCW Reg. VI/2  (5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  // PSCRB — Proficiency in Survival Craft & Rescue Boats
  {
    id: 'pscrb',
    test: /proficiency in survival craft|\brpscrb\b|\bpscrb\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/2-1: 5-year refresher for Proficiency in Survival Craft & Rescue Boats required.',
  },

  // FRB / PFRB — Proficiency in Fast Rescue Boats (STCW Reg. VI/2-2)
  {
    id: 'frb',
    test: /fast rescue boat|\bpfrb\b|\bfrb\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/2-2: 5-year refresher for Proficiency in Fast Rescue Boats required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MEDICAL — STCW Reg. VI/4
  // ═══════════════════════════════════════════════════════════════════════════

  // MFA — Medical First Aid (STCW Table A-VI/4-1)
  // DG Shipping MS Notice 13 of 2020, cited again in DGS Training Circular
  // No. 04 of 2025, clarifies Medical First Aid and Medical Care do not require
  // five-year revalidation under STCW 2010 as amended. Some employers/flags may
  // still require refreshers, so keep these as verify unless an explicit expiry
  // exists on the source record.
  {
    id: 'medical_first_aid',
    test: /medical first aid|\bmfa\b|\bmefa\b/i,
    lifecycle: 'no_five_year_revalidation',
    actionLabel: 'Verify requirement',
    requirementNote:
      'STCW Reg. VI/4-1: Medical First Aid. DG Shipping MS Notice 13 of 2020 clarifies no STCW five-year revalidation requirement; verify employer or flag-state requirement.',
  },

  // Medical Care / MCAS (STCW Table A-VI/4-2)
  {
    id: 'medical_care',
    test: /medical care officer|medical care(?! first)|\bmcas\b/i,
    lifecycle: 'no_five_year_revalidation',
    actionLabel: 'Verify requirement',
    requirementNote:
      'STCW Reg. VI/4-2: Medical Care. DG Shipping MS Notice 13 of 2020 clarifies no STCW five-year revalidation requirement; verify employer or flag-state requirement.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SECURITY — STCW Reg. VI/5-6  (refresher or revalidation, 5 years)
  // ═══════════════════════════════════════════════════════════════════════════

  // SSO — Ship Security Officer (STCW Reg. VI/5)
  // Must appear before the generic security-awareness rule below.
  {
    id: 'sso',
    test: /ship security officer|\bsso\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/5: 5-year refresher for Ship Security Officer certificate of proficiency required.',
  },

  // Security Awareness / STSDSD / ISPS (STCW Reg. VI/6-1 & VI/6-2)
  {
    id: 'security_awareness',
    test: /security awareness|designated security duties|\bstsdsd\b|\bisps\b|ship security(?! officer)/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. VI/6: 5-year refresher in Security Awareness / Designated Security Duties required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RADIO / GMDSS — STCW Reg. IV/2  (5-year revalidation)
  // ═══════════════════════════════════════════════════════════════════════════

  // GOC/ROC/GMDSS — revalidation linked to CoC or sea service
  {
    id: 'gmdss',
    test: /\bgoc\b|general operator certificate|\broc\b|restricted operator certificate|\bgmdss\b|global maritime distress/i,
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    actionLabel: 'Revalidation due',
    requirementNote:
      'STCW Reg. IV/2: GMDSS operator certificate must be revalidated every 5 years — confirm sea service and refresher training.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TANKER & CARGO — STCW Reg. V/1  (5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  // Dangerous Goods / IMDG / IMSBC — must appear before generic tanker rule
  {
    id: 'dangerous_goods',
    test: /dangerous goods|\bimdg\b|\bimsbc\b|imo dg\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW / IMDG Code: 5-year refresher in Dangerous Goods handling required.',
  },

  // Tanker training — basic and advanced (oil, chemical, gas / LPG / LNG)
  {
    id: 'tanker',
    test: /tanker|\batot\b|\batct\b|\batgt\b|oil tanker|chemical tanker|gas tanker|liquefied gas|lpg tanker|lng tanker/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. V/1: 5-year refresher for tanker training endorsement required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION / BRIDGE — STCW Reg. II  (5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  // ECDIS — Electronic Chart Display and Information System
  {
    id: 'ecdis',
    test: /\becdis\b|electronic chart display/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II: 5-year refresher in ECDIS proficiency required.',
  },

  // ARPA / Radar — operational and management level
  {
    id: 'arpa',
    test: /\barpa\b|radar simulator|radar observer|radar.*(?:operational|management)|(?:operational|management).*radar/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II: 5-year refresher in ARPA/Radar proficiency required.',
  },

  // BRM — Bridge Resource Management
  {
    id: 'brm',
    test: /bridge resource management|\bbrm\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II: 5-year refresher in Bridge Resource Management required.',
  },

  // SAR / Search and Rescue Coordinator
  {
    id: 'sar',
    test: /search and rescue|\bsar\b.*coordinator|coordinator.*\bsar\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II: 5-year refresher in Search and Rescue coordination required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENGINE / TECHNICAL — STCW Reg. III  (5-year refresher / revalidation)
  // ═══════════════════════════════════════════════════════════════════════════

  // ERM — Engine Room Resource Management
  {
    id: 'erm',
    test: /engine r(?:oom|esource) (?:resource|management)|\berm\b|\berrm\b/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. III: 5-year refresher in Engine Room Resource Management required.',
  },

  // ETO — Electro-Technical Officer (STCW Reg. III/6) — CoC equivalent, revalidation
  {
    id: 'eto',
    test: /electro.technical officer|electro.technical course|\beto\b/i,
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    actionLabel: 'Revalidation due',
    requirementNote:
      'STCW Reg. III/6: ETO certificate of competency must be revalidated every 5 years.',
  },

  // High Voltage / HV Training
  {
    id: 'high_voltage',
    test: /high voltage|\bhv\b.*train|hv.*(?:course|safety)/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. III: 5-year refresher in High Voltage safety required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PASSENGER SHIP — STCW Reg. V/2  (5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  // CMHB — Crisis Management & Human Behaviour (supersedes Crowd Management)
  {
    id: 'cmhb',
    test: /crisis management.*human|human.*behaviour.*crisis|\bcmhb\b|passenger ship.*crisis/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Crisis Management & Human Behaviour required for passenger ship officers.',
  },

  // Crowd Management (superseded by CMHB for officers, but still required for ratings)
  {
    id: 'crowd_management',
    test: /crowd management/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Crowd Management required for passenger ship personnel.',
  },

  // Passenger Safety / Passenger Ship Safety
  {
    id: 'passenger_safety',
    test: /passenger.{0,10}safety|safety.{0,10}passenger/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. V/2: 5-year refresher in Passenger Safety required.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LEADERSHIP & MANAGEMENT — STCW Reg. II / III  (5-year refresher)
  // ═══════════════════════════════════════════════════════════════════════════

  // Leadership & Managerial Skills (management level — supersedes Leadership & Teamwork)
  {
    id: 'leadership_managerial',
    test: /leadership.*managerial|managerial.*skill/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II/2 / III/2: 5-year refresher in Leadership & Managerial Skills required for management-level officers.',
  },

  // Leadership & Teamwork (operational level)
  {
    id: 'leadership_teamwork',
    test: /leadership.*teamwork|teamwork.*(?:skills|leadership)/i,
    lifecycle: 'refresher_required',
    cycleYears: 5,
    actionLabel: 'Refresher due',
    requirementNote:
      'STCW Reg. II/1 / III/1: 5-year refresher in Leadership & Teamworking Skills required for operational-level officers.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CERTIFICATES OF COMPETENCY — STCW Reg. I/11  (5-year revalidation)
  // This is a broad catch-all for CoC and must appear after all more-specific rules.
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: 'coc',
    test: /certificate of competency|\bcoc\b|class\s*[i1-4iv]+\s*(?:deck|unlimited|near coastal|eng)|(?:master|chief officer|chief mate|chief engineer|second engineer|officer of the watch|\boow\b)/i,
    lifecycle: 'revalidation_required',
    cycleYears: 5,
    actionLabel: 'Revalidation due',
    requirementNote:
      'STCW Reg. I/11: CoC revalidation required every 5 years — confirm 12 months sea service, medical fitness, and proficiency refreshers.',
  },
];

export const UNKNOWN_VERIFY_RULE: LifecycleRule = {
  id: 'unknown_verify',
  test: /.*/,
  lifecycle: 'unknown_verify',
  actionLabel: 'Verify requirement',
  requirementNote:
    'No deterministic Seamigo lifecycle rule matched this record. Verify renewal requirements with the issuing authority, employer, or flag administration.',
};

// ── Core classification function ─────────────────────────────────────────────

/**
 * Classify a certificate name against lifecycle rules.
 *
 * @param name - The certificate/course name from the DG profile.
 * @param hasExplicitExpiry - True when the DG profile includes a parsed validUpto date.
 *   Explicit dates override only rules that opt in with honorExplicitExpiry.
 * @returns The matching LifecycleRule, or null → use standard expiry_date_based logic.
 */
export function classifyLifecycle(
  name: string,
  hasExplicitExpiry: boolean,
): LifecycleRule | null {
  const catalogMatch = normalizeCertificateName(name);
  if (catalogMatch) {
    const entry = catalogMatch.entry;
    if (entry.lifecycle === 'no_expiry' && entry.honorExplicitExpiry && hasExplicitExpiry) {
      return null;
    }

    return {
      id: entry.canonicalId,
      test: /.*/,
      lifecycle: entry.lifecycle,
      honorExplicitExpiry: entry.honorExplicitExpiry,
      cycleYears: entry.cycleYears,
      actionLabel:
        entry.lifecycle === 'revalidation_required'
          ? 'Revalidation due'
          : entry.lifecycle === 'no_expiry'
          ? 'No expiry'
          : entry.lifecycle === 'no_five_year_revalidation' || entry.lifecycle === 'unknown_verify'
          ? 'Verify requirement'
          : entry.lifecycle === 'expiry_date_based'
          ? 'Renewal due'
          : 'Refresher due',
      requirementNote: entry.requirementNote,
    };
  }

  for (const rule of LIFECYCLE_RULES) {
    if (!rule.test.test(name)) continue;

    // Honor explicit expiry dates only for rules that opt in. INDoS stays lifetime;
    // EXN-45 falls back to date-based handling when the source record has an expiry.
    if (rule.lifecycle === 'no_expiry' && rule.honorExplicitExpiry && hasExplicitExpiry) {
      return null;
    }

    return rule;
  }
  return null;
}

// ── UI label resolution ──────────────────────────────────────────────────────

type BaseStatus = 'valid' | 'expiring-soon' | 'critical' | 'expired';

/**
 * Derive the display label shown in the status badge, given a matched rule and
 * the computed expiry status. Returns the most specific label for the situation.
 */
export function resolveActionLabel(rule: LifecycleRule, status: BaseStatus): string {
  switch (rule.lifecycle) {
    case 'no_expiry':
      return 'No expiry';
    case 'no_five_year_revalidation':
      return 'Verify requirement';
    case 'unknown_verify':
      return 'Verify requirement';
    case 'expiry_date_based':
      if (status === 'expired' || status === 'critical') return 'Renewal due';
      if (status === 'expiring-soon') return 'Renewal soon';
      return 'Valid';
    case 'refresher_required':
      if (status === 'expired' || status === 'critical') return 'Refresher due';
      if (status === 'expiring-soon') return 'Refresher soon';
      return 'Valid';
    case 'revalidation_required':
      if (status === 'expired' || status === 'critical') return 'Revalidation due';
      if (status === 'expiring-soon') return 'Revalidation soon';
      return 'Valid';
    default:
      return rule.actionLabel;
  }
}

// ── Pure predicate utilities (testable without DOM) ──────────────────────────

/** True if the lifecycle means the cert never needs renewal. */
export function isNoExpiry(lifecycle: LifecycleType): boolean {
  return lifecycle === 'no_expiry';
}

/** True if the cert requires an active refresher or revalidation to stay current. */
export function requiresRenewal(lifecycle: LifecycleType): boolean {
  return lifecycle === 'refresher_required' || lifecycle === 'revalidation_required';
}

/**
 * True if the cert should be excluded from urgent alert strips and expired counts.
 * Applies to permanent records and to certs whose renewal cycle is ambiguous.
 */
export function isExcludedFromAlerts(lifecycle: LifecycleType): boolean {
  return lifecycle === 'no_expiry' || lifecycle === 'no_five_year_revalidation' || lifecycle === 'unknown_verify';
}
